import { useState, useCallback, useRef, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import * as Popover from "@radix-ui/react-popover";
import {
  useBiblioRecord,
  useLocations,
  useCreateItem,
  useUpdateItem,
  useRdaVocabularies,
} from "@/api/hooks";
import type {
  ItemSummary,
  Contribution,
  RdaVocabularyEntry,
  RecordStatus,
  CreateItemInput,
  ItemStatus,
  Work,
} from "@/api/types";
import { RECORD_TYPE_LABELS } from "@/lib/recordTypes";
import { BookCover } from "@/features/portal/components";
import { ItemDetailDialog } from "../components/ItemDetailDialog";
import { WorkRelationsGraph } from "../components";

// Status configuration with labels, colors and icons
const STATUS_CONFIG: Record<
  ItemStatus,
  {
    label: string;
    color: string;
    icon: React.ReactNode;
    /** Can be manually changed to/from this status */
    editable: boolean;
  }
> = {
  available: {
    label: "Disponible",
    color: "green",
    icon: (
      <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
    ),
    editable: true,
  },
  on_loan: {
    label: "En prêt",
    color: "yellow",
    icon: (
      <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    ),
    editable: false, // Managed by circulation
  },
  reserved: {
    label: "Réservé",
    color: "blue",
    icon: (
      <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"
        />
      </svg>
    ),
    editable: false, // Managed by circulation
  },
  in_transit: {
    label: "En transit",
    color: "purple",
    icon: (
      <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M13 7l5 5m0 0l-5 5m5-5H6"
        />
      </svg>
    ),
    editable: false, // Managed by circulation
  },
  in_processing: {
    label: "En traitement",
    color: "gray",
    icon: (
      <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
        />
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
        />
      </svg>
    ),
    editable: true,
  },
  damaged: {
    label: "Endommagé",
    color: "orange",
    icon: (
      <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
        />
      </svg>
    ),
    editable: true,
  },
  lost: {
    label: "Perdu",
    color: "red",
    icon: (
      <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    ),
    editable: true,
  },
  withdrawn: {
    label: "Retiré",
    color: "red",
    icon: (
      <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
        />
      </svg>
    ),
    editable: true,
  },
};

export function RecordDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: record, isLoading, error, refetch } = useBiblioRecord(id);
  const { data: rdaVocabularies } = useRdaVocabularies();
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [openItemId, setOpenItemId] = useState<string | null>(null);

  const handleItemAdded = useCallback(() => {
    void refetch();
  }, [refetch]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error || !record) {
    return (
      <div className="rounded-lg bg-red-100 p-4 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400">
        Notice introuvable ou erreur de chargement.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
        <Link to="/catalog" className="hover:text-blue-600 dark:hover:text-blue-400">
          Catalogue
        </Link>
        <span>›</span>
        <span className="truncate text-gray-900 dark:text-white">{record.title}</span>
      </nav>

      {/* Hero Section - Cover + Main Info */}
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
        <div className="flex flex-col sm:flex-row">
          {/* Cover */}
          <div className="flex shrink-0 items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-6 sm:w-48 dark:from-gray-800 dark:to-gray-900">
            <BookCover
              coverUrl={record.coverUrl}
              alt={`Couverture de ${record.title}`}
              type={record.type}
              size="md"
              className="shadow-lg"
            />
          </div>

          {/* Main Info */}
          <div className="flex flex-1 flex-col justify-between p-6">
            <div>
              {/* Badges */}
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                  {RECORD_TYPE_LABELS[record.type]}
                </span>
                <StatusBadge status={record.status} />
                {record.publicationYear && (
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {record.publicationYear}
                  </span>
                )}
              </div>

              {/* Title */}
              <h1 className="mt-3 text-2xl font-bold text-gray-900 dark:text-white">
                {record.title}
              </h1>
              {record.subtitle && (
                <p className="mt-1 text-lg text-gray-600 dark:text-gray-400">{record.subtitle}</p>
              )}

              {/* Authors */}
              {record.contributions.length > 0 && (
                <p className="mt-3 text-gray-700 dark:text-gray-300">
                  {record.contributions
                    .filter((c) => c.role === "author")
                    .map((c) => c.authority.preferredName)
                    .join(", ") || record.authorsDisplay}
                </p>
              )}

              {/* Quick Info */}
              <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm text-gray-500 dark:text-gray-400">
                {record.publisher && (
                  <span>
                    <span className="font-medium text-gray-700 dark:text-gray-300">Éditeur :</span>{" "}
                    {record.publisher}
                  </span>
                )}
                {record.isbn && (
                  <span>
                    <span className="font-medium text-gray-700 dark:text-gray-300">ISBN :</span>{" "}
                    {formatIsbn(record.isbn)}
                  </span>
                )}
                {record.pageCount && (
                  <span>
                    <span className="font-medium text-gray-700 dark:text-gray-300">Pages :</span>{" "}
                    {record.pageCount}
                  </span>
                )}
              </div>
            </div>

            {/* Action */}
            <div className="mt-4 flex items-center gap-3">
              <Link
                to={`/catalog/${id ?? ""}/edit`}
                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                  />
                </svg>
                Modifier
              </Link>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {record.itemsCount} exemplaire{record.itemsCount > 1 ? "s" : ""} •{" "}
                {record.availableItemsCount} disponible{record.availableItemsCount > 1 ? "s" : ""}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Left Column */}
        <div className="space-y-6 lg:col-span-2">
          {/* Summary */}
          {record.summary && (
            <Section title="Résumé">
              <p className="leading-relaxed text-gray-700 dark:text-gray-300">{record.summary}</p>
            </Section>
          )}

          {/* Work Relations Graph (IFLA LRM) */}
          {record.work && record.work.relatedWorks.length > 0 && (
            <Section
              title="Réseau des œuvres liées"
              action={
                <Link
                  to={`/catalog/works/${record.work.id}`}
                  className="text-sm font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  Voir l'œuvre →
                </Link>
              }
            >
              <WorkRelationsGraph
                work={
                  {
                    id: record.work.id,
                    preferredTitle: record.work.preferredTitle,
                    relatedWorks: record.work.relatedWorks,
                  } as Work
                }
                onNodeClick={(workId) => {
                  void navigate(`/catalog/works/${workId}`);
                }}
              />
            </Section>
          )}

          {/* Details Grid */}
          <Section title="Informations">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {record.language && (
                <InfoCard label="Langue" value={getLanguageName(record.language)} />
              )}
              {record.edition && <InfoCard label="Édition" value={record.edition} />}
              {record.series && (
                <InfoCard
                  label="Collection"
                  value={
                    record.seriesNumber
                      ? `${record.series}, n° ${record.seriesNumber}`
                      : record.series
                  }
                />
              )}
              {record.issn && <InfoCard label="ISSN" value={formatIssn(record.issn)} />}
              {record.persistentIdentifier && (
                <PersistentIdentifierCard identifier={record.persistentIdentifier} />
              )}
              {record.classification && (
                <InfoCard label="Classification" value={record.classification} />
              )}
            </div>
          </Section>

          {/* RDA-FR types: always shown so missing elements are visible */}
          <Section title="Contenu et support (RDA-FR)">
            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              {RDA_ELEMENTS.map(({ field, label }) => (
                <div key={field} className="rounded-lg bg-gray-50 p-3 dark:bg-gray-700/50">
                  <dt className="text-xs text-gray-500 dark:text-gray-400">{label}</dt>
                  <dd className="mt-1 text-sm font-medium text-gray-900 dark:text-white">
                    {record[field].length > 0 ? (
                      rdaLabels(record[field], rdaVocabularies?.[field])
                    ) : (
                      <span className="font-normal text-gray-500 italic dark:text-gray-400">
                        Non renseigné
                      </span>
                    )}
                  </dd>
                </div>
              ))}
            </dl>
            {(record.rdaSuggestion ||
              RDA_ELEMENTS.some(({ field }) => record[field].length === 0)) && (
              <p className="mt-4 flex flex-wrap items-baseline gap-x-2 text-sm text-gray-600 dark:text-gray-400">
                <span>
                  {record.rdaSuggestion
                    ? "Des valeurs conformes à la Transition bibliographique peuvent être proposées pour les éléments non renseignés."
                    : "Notice incomplète au regard de la Transition bibliographique."}
                </span>
                <Link
                  to={`/catalog/${id ?? ""}/edit`}
                  className="rounded font-medium text-blue-700 underline-offset-2 hover:underline focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none dark:text-blue-400"
                >
                  Compléter la notice
                </Link>
              </p>
            )}
          </Section>

          {/* Contributors */}
          {record.contributions.length > 0 && (
            <Section title="Contributeurs">
              <ContributorsList contributions={record.contributions} />
            </Section>
          )}

          {/* Subjects */}
          {record.subjects.length > 0 && (
            <Section title="Sujets">
              <div className="flex flex-wrap gap-2">
                {record.subjects.map((subject, i) => (
                  <span
                    key={i}
                    className="rounded-full bg-blue-50 px-3 py-1 text-sm font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
                  >
                    {subject}
                  </span>
                ))}
              </div>
            </Section>
          )}
        </div>

        {/* Right Column - Items */}
        <div>
          <Section title={`Exemplaires (${String(record.itemsCount)})`}>
            <ItemsList
              items={record.items}
              onAddItem={() => {
                setShowAddItemModal(true);
              }}
              onItemUpdated={handleItemAdded}
              onOpenItem={setOpenItemId}
            />
          </Section>
        </div>
      </div>

      {/* Add Item Modal */}
      <AddItemModal
        isOpen={showAddItemModal}
        onClose={() => {
          setShowAddItemModal(false);
        }}
        recordId={record.id}
        onSuccess={handleItemAdded}
      />

      {openItemId && (
        <ItemDetailDialog
          itemId={openItemId}
          onClose={() => {
            setOpenItemId(null);
          }}
          onUpdated={handleItemAdded}
        />
      )}
    </div>
  );
}

function Section({
  title,
  children,
  action,
}: {
  title: string;
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-sm font-semibold tracking-wide text-gray-900 uppercase dark:text-white">
          {title}
        </h2>
        {action}
      </div>
      {children}
    </div>
  );
}

const RDA_ELEMENTS = [
  { field: "contentTypes", label: "Type de contenu" },
  { field: "mediaTypes", label: "Type de médiation" },
  { field: "carrierTypes", label: "Type de support" },
] as const;

/**
 * French labels of RDA-FR codes; falls back to the codes while the vocabulary
 * loads or for codes it does not know.
 */
function rdaLabels(codes: string[], vocabulary: RdaVocabularyEntry[] | undefined): string {
  return codes
    .map((code) => vocabulary?.find((entry) => entry.code === code)?.label ?? code)
    .join(", ");
}

function InfoCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg bg-gray-50 p-3 dark:bg-gray-700/50">
      <dt className="text-xs font-medium text-gray-500 uppercase dark:text-gray-400">{label}</dt>
      <dd className="mt-1 text-sm font-medium text-gray-900 dark:text-white">{value}</dd>
    </div>
  );
}

/**
 * UNIMARC 003. A BnF ARK URI is shown as its ARK and linked to the BnF record.
 */
function PersistentIdentifierCard({ identifier }: { identifier: string }) {
  const ark = /^https?:\/\/catalogue\.bnf\.fr\/(ark:\/12148\/\w+)$/.exec(identifier)?.[1];
  const isUrl = /^https?:\/\//.test(identifier);

  return (
    <div className="rounded-lg bg-gray-50 p-3 dark:bg-gray-700/50">
      <dt className="text-xs font-medium text-gray-500 uppercase dark:text-gray-400">
        {ark ? "Identifiant ARK BnF" : "Identifiant pérenne"}
      </dt>
      <dd className="mt-1 text-sm font-medium break-all text-gray-900 dark:text-white">
        {isUrl ? (
          <a
            href={identifier}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded text-blue-700 underline-offset-2 hover:underline focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none dark:text-blue-400"
          >
            {ark ?? identifier}
            <span className="sr-only">
              {ark ? " (notice au catalogue de la BnF, nouvel onglet)" : " (nouvel onglet)"}
            </span>
          </a>
        ) : (
          identifier
        )}
      </dd>
    </div>
  );
}

function StatusBadge({ status }: { status: RecordStatus }) {
  const statusConfig: Record<RecordStatus, { label: string; className: string }> = {
    draft: {
      label: "Brouillon",
      className: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400",
    },
    validated: {
      label: "Validée",
      className: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400",
    },
    archived: {
      label: "Archivée",
      className: "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400",
    },
  };

  const config = statusConfig[status];

  return (
    <span className={`rounded-md px-2 py-1 text-xs font-medium ${config.className}`}>
      {config.label}
    </span>
  );
}

function ContributorsList({ contributions }: { contributions: Contribution[] }) {
  const byRole = contributions.reduce<Record<string, Contribution[]>>((acc, c) => {
    const role = c.roleLabel;
    if (!acc[role]) acc[role] = [];
    acc[role].push(c);
    return acc;
  }, {});

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      {Object.entries(byRole).map(([role, contribs]) => (
        <div key={role} className="rounded-lg bg-gray-50 p-3 dark:bg-gray-700/50">
          <h3 className="text-xs font-medium text-gray-500 uppercase dark:text-gray-400">{role}</h3>
          <div className="mt-2 space-y-1">
            {contribs.map((c) => (
              <Link
                key={c.id}
                to={`/catalog/authorities/${c.authority.id}`}
                className="block text-sm font-medium text-gray-900 hover:text-blue-600 dark:text-white dark:hover:text-blue-400"
              >
                {c.authority.preferredName}
              </Link>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// Color classes for status badges
const STATUS_COLOR_CLASSES: Record<string, string> = {
  green: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
  yellow: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  red: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  blue: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  purple: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
  orange: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
  gray: "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-400",
};

// Interactive status control component
function ItemStatusControl({
  item,
  onStatusChange,
}: {
  item: ItemSummary;
  onStatusChange: () => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const updateItem = useUpdateItem(item.id);
  const successTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const statusConfig = STATUS_CONFIG[item.status];

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
    };
  }, []);

  const handleStatusChange = async (newStatus: ItemStatus) => {
    if (newStatus === item.status) {
      setIsOpen(false);
      return;
    }

    setIsUpdating(true);
    try {
      await updateItem.mutateAsync({ status: newStatus });
      setIsOpen(false);
      setShowSuccess(true);
      onStatusChange();

      // Clear success animation after delay
      successTimeoutRef.current = setTimeout(() => {
        setShowSuccess(false);
      }, 1500);
    } catch {
      // Error handling could be added here
    } finally {
      setIsUpdating(false);
    }
  };

  const colorClass =
    STATUS_COLOR_CLASSES[statusConfig.color] ??
    "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-400";

  // Non-editable statuses (managed by circulation) show as static badges
  if (!statusConfig.editable) {
    return (
      <span
        className={`inline-flex shrink-0 items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium ${colorClass}`}
        title="Statut géré par la circulation"
      >
        {statusConfig.icon}
        <span>{statusConfig.label}</span>
      </span>
    );
  }

  return (
    <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
      <Popover.Trigger asChild>
        <button
          className={`group relative inline-flex shrink-0 items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium transition-all duration-200 ${colorClass} ${
            showSuccess
              ? "ring-2 ring-emerald-400 ring-offset-1 dark:ring-offset-gray-800"
              : "hover:ring-2 hover:ring-blue-400/50 hover:ring-offset-1 dark:hover:ring-offset-gray-800"
          }`}
          disabled={isUpdating}
        >
          {/* Success checkmark animation */}
          {showSuccess && (
            <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500 text-white">
              <svg className="h-2.5 w-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={3}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </span>
          )}

          {isUpdating ? (
            <svg
              className="h-3 w-3 animate-spin"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
          ) : (
            statusConfig.icon
          )}
          <span>{statusConfig.label}</span>
          {/* Dropdown indicator */}
          <svg
            className="h-3 w-3 opacity-40 transition-transform group-hover:opacity-70"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          className="animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 w-56"
          sideOffset={5}
          align="end"
        >
          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800">
            {/* Header */}
            <div className="border-b border-gray-100 px-3 py-2 dark:border-gray-700">
              <p className="text-xs font-semibold tracking-wide text-gray-500 uppercase dark:text-gray-400">
                Changer le statut
              </p>
            </div>

            {/* Status options */}
            <div className="p-2">
              <div className="grid grid-cols-2 gap-1.5">
                {(
                  Object.entries(STATUS_CONFIG) as [
                    ItemStatus,
                    (typeof STATUS_CONFIG)[ItemStatus],
                  ][]
                )
                  .filter(([, config]) => config.editable)
                  .map(([status, config]) => {
                    const isSelected = status === item.status;
                    const optionColorClass =
                      STATUS_COLOR_CLASSES[config.color] ??
                      "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-400";

                    return (
                      <button
                        key={status}
                        onClick={() => void handleStatusChange(status)}
                        disabled={isUpdating}
                        className={`flex flex-col items-center gap-1.5 rounded-lg p-2.5 text-center transition-all duration-150 ${
                          isSelected
                            ? `${optionColorClass} ring-2 ring-blue-500 ring-offset-1 dark:ring-offset-gray-800`
                            : "bg-gray-50 text-gray-600 hover:bg-gray-100 dark:bg-gray-700/50 dark:text-gray-300 dark:hover:bg-gray-700"
                        }`}
                      >
                        <span
                          className={`flex h-8 w-8 items-center justify-center rounded-full ${optionColorClass}`}
                        >
                          {config.icon}
                        </span>
                        <span className="text-[11px] leading-tight font-medium">
                          {config.label}
                        </span>
                        {isSelected && (
                          <span className="text-[10px] font-normal text-blue-600 dark:text-blue-400">
                            Actuel
                          </span>
                        )}
                      </button>
                    );
                  })}
              </div>
            </div>

            {/* Helper text */}
            <div className="border-t border-gray-100 px-3 py-2 dark:border-gray-700">
              <p className="text-[10px] text-gray-400 dark:text-gray-500">
                Les statuts "En prêt", "Réservé" et "En transit" sont gérés automatiquement par la
                circulation.
              </p>
            </div>
          </div>

          <Popover.Arrow className="fill-white dark:fill-gray-800" />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

function ItemsList({
  items,
  onAddItem,
  onItemUpdated,
  onOpenItem,
}: {
  items: ItemSummary[];
  onAddItem: () => void;
  onItemUpdated: () => void;
  onOpenItem: (itemId: string) => void;
}) {
  if (items.length === 0) {
    return (
      <div className="py-8 text-center">
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700">
          <svg
            className="h-6 w-6 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
            />
          </svg>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400">Aucun exemplaire</p>
        <button
          onClick={onAddItem}
          className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400"
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Ajouter un exemplaire
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {items.map((item) => (
        <div
          key={item.id}
          className="rounded-lg border border-gray-100 bg-gray-50 p-3 transition-colors hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-700/50 dark:hover:bg-gray-700"
        >
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <button
                type="button"
                onClick={() => {
                  onOpenItem(item.id);
                }}
                aria-label={`Ouvrir la fiche de l'exemplaire ${item.barcode}`}
                className="block max-w-full truncate rounded font-mono text-sm font-medium text-gray-900 underline-offset-2 hover:text-blue-600 hover:underline focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none dark:text-white dark:hover:text-blue-400"
              >
                {item.barcode}
              </button>
              {item.callNumber && (
                <p className="mt-0.5 truncate text-xs text-gray-500 dark:text-gray-400">
                  {item.callNumber}
                </p>
              )}
              <p className="mt-0.5 truncate text-xs text-gray-500 dark:text-gray-400">
                📍 {item.location.name}
              </p>
            </div>
            <ItemStatusControl item={item} onStatusChange={onItemUpdated} />
          </div>
        </div>
      ))}
      <button
        onClick={onAddItem}
        className="flex w-full items-center justify-center gap-1 rounded-lg border-2 border-dashed border-gray-200 py-3 text-sm font-medium text-gray-500 transition-colors hover:border-blue-400 hover:text-blue-600 dark:border-gray-600 dark:hover:border-blue-500 dark:hover:text-blue-400"
      >
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
        Ajouter
      </button>
    </div>
  );
}

// Modal for adding a new item
function AddItemModal({
  isOpen,
  onClose,
  recordId,
  onSuccess,
}: {
  isOpen: boolean;
  onClose: () => void;
  recordId: string;
  onSuccess: () => void;
}) {
  const { data: locations } = useLocations();
  const createItem = useCreateItem();
  const [formData, setFormData] = useState({
    barcode: "",
    locationId: "",
    callNumber: "",
  });
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError(null);

      if (!formData.barcode.trim()) {
        setError("Le code-barres est obligatoire");
        return;
      }
      if (!formData.locationId) {
        setError("La localisation est obligatoire");
        return;
      }

      try {
        const input: CreateItemInput = {
          biblioRecordId: recordId,
          barcode: formData.barcode.trim(),
          locationId: formData.locationId,
          status: "available",
          loanable: true,
          reservable: true,
        };
        if (formData.callNumber.trim()) {
          input.callNumber = formData.callNumber.trim();
        }
        await createItem.mutateAsync(input);
        setFormData({ barcode: "", locationId: "", callNumber: "" });
        onSuccess();
        onClose();
      } catch {
        setError("Erreur lors de la création de l'exemplaire");
      }
    },
    [formData, recordId, createItem, onSuccess, onClose]
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-md rounded-xl bg-white p-6 shadow-xl dark:bg-gray-800">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Ajouter un exemplaire
        </h3>

        <form onSubmit={(e) => void handleSubmit(e)} className="mt-4 space-y-4">
          {error && (
            <div className="rounded-lg bg-red-100 p-3 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-400">
              {error}
            </div>
          )}

          <div>
            <label
              htmlFor="barcode"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              Code-barres *
            </label>
            <input
              type="text"
              id="barcode"
              value={formData.barcode}
              onChange={(e) => {
                setFormData((prev) => ({ ...prev, barcode: e.target.value }));
              }}
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              placeholder="Ex: 3700123456789"
              autoFocus
            />
          </div>

          <div>
            <label
              htmlFor="location"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              Localisation *
            </label>
            <select
              id="location"
              value={formData.locationId}
              onChange={(e) => {
                setFormData((prev) => ({ ...prev, locationId: e.target.value }));
              }}
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            >
              <option value="">Sélectionner...</option>
              {locations?.map((loc) => (
                <option key={loc.id} value={loc.id}>
                  {loc.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label
              htmlFor="callNumber"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              Cote
            </label>
            <input
              type="text"
              id="callNumber"
              value={formData.callNumber}
              onChange={(e) => {
                setFormData((prev) => ({ ...prev, callNumber: e.target.value }));
              }}
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              placeholder="Ex: 843 TRE"
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={createItem.isPending}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {createItem.isPending ? "Création..." : "Ajouter"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Helper functions
function getLanguageName(code: string): string {
  const languages: Record<string, string> = {
    fre: "Français",
    eng: "Anglais",
    ger: "Allemand",
    spa: "Espagnol",
    ita: "Italien",
    por: "Portugais",
    dut: "Néerlandais",
    rus: "Russe",
    chi: "Chinois",
    jpn: "Japonais",
    ara: "Arabe",
    lat: "Latin",
    grc: "Grec ancien",
  };
  return languages[code] ?? code.toUpperCase();
}

function formatIsbn(isbn: string): string {
  if (isbn.length === 13) {
    return `${isbn.slice(0, 3)}-${isbn.charAt(3)}-${isbn.slice(4, 7)}-${isbn.slice(7, 12)}-${isbn.charAt(12)}`;
  }
  if (isbn.length === 10) {
    return `${isbn.charAt(0)}-${isbn.slice(1, 4)}-${isbn.slice(4, 9)}-${isbn.charAt(9)}`;
  }
  return isbn;
}

function formatIssn(issn: string): string {
  if (issn.length === 8) {
    return `${issn.slice(0, 4)}-${issn.slice(4)}`;
  }
  return issn;
}

export default RecordDetailPage;
